import { useEffect, useRef, useState } from 'react';
import { getFeishuSyncStatus, retryFeishuSync, type FeishuSyncStatus } from '../../lib/api';
import '../../styles/feishu-sync.css';

const labels = { pending: '等待同步', running: '同步中', success: '已同步', partial: '部分同步失败', failed: '同步失败', repairing: '正在恢复关联' };
const time = (value: number | null) => value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '';
export default function FeishuSyncPanel({ surveyId }: { surveyId: string }) {
  const [data, setData] = useState<FeishuSyncStatus | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    const version = ++generation.current;
    setData(null); setError(''); setBusy(false);
    if (!surveyId) return;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const next = await getFeishuSyncStatus(surveyId);
        if (version !== generation.current) return;
        setData(next); setError('');
      } catch (e: any) {
        if (version === generation.current) setError(e.status === 401 ? '登录已过期，请重新登录后查看同步状态。' : '暂时无法读取同步状态，请稍后重试。');
      }
      if (version === generation.current) timer = setTimeout(refresh, 5000);
    };
    void refresh();
    return () => { ++generation.current; clearTimeout(timer); };
  }, [surveyId]);
  const retry = async (rebuild = false) => {
    const version = generation.current;
    setBusy(true); setError('');
    try {
      const next = await retryFeishuSync(surveyId, rebuild);
      if (version === generation.current) setData(next);
    } catch (e: any) {
      if (version === generation.current) setError(e.body?.message || '操作未完成，请稍后重试。');
    } finally { if (version === generation.current) setBusy(false); }
  };
  if (!surveyId) return <div className="feishu-sync feishu-sync-empty">选择一份问卷，可查看飞书同步结果并补同步历史答卷。</div>;
  const active = data?.status === 'running' || data?.status === 'pending' || data?.status === 'repairing';
  const deleted = /1002.*deleted/i.test(data?.lastError ?? '');
  return <section className="feishu-sync" aria-label="飞书同步">
    <div className="feishu-sync-main">
      <div className="feishu-sync-heading"><strong>飞书同步</strong><span className={`feishu-sync-state is-${data?.status ?? 'pending'}`} role="status" aria-atomic="true">{data ? (data.configured ? labels[data.status] : '未配置') : '读取状态中…'}</span></div>
      <div className="feishu-sync-actions">
        {data?.url && <a href={data.url} target="_blank" rel="noreferrer">打开飞书表 ↗</a>}
        <button className="btn" disabled={!data?.configured || busy || active} onClick={() => void retry()}>{busy ? '提交中…' : active ? '正在等待同步完成' : '核对并补同步'}</button>
        {deleted && <button className="btn" disabled={busy || active} onClick={() => void retry(true)}>重建已删除的关联表</button>}
      </div>
    </div>
    {data && <p className="feishu-sync-description">{data.status === 'running'
      ? `正在核对 ${data.summary.processed ?? 0} / ${data.summary.total} 条答卷`
      : data.status === 'pending' ? '任务已保存，系统将自动核对名称、字段、答案、附件和答卷状态。'
      : data.status === 'success' ? `${data.summary.success ?? 0} / ${data.summary.total} 条答卷已核对同步`
      : `共 ${data.summary.total} 条答卷，已完成 ${data.summary.success ?? 0} 条。失败项会自动重试。`}
      {data.completedAt && <> · 最近核对：{time(data.completedAt)}</>}
    </p>}
    {!!data?.summary.unmatched && <p className="feishu-sync-note">飞书另有 {data.summary.unmatched} 条历史记录未匹配到平台答卷，已保留并标记“待核对”，不计入当前答卷序号。</p>}
    {(error || data?.lastError) && <p className="feishu-sync-error" role="alert">{error || data?.lastError}</p>}
    {!!data?.records.length && <details><summary>查看未完成的答卷（最多显示20条）</summary><ul>{data.records.map(r => <li key={r.sequence}>第 {r.sequence} 条：{r.error || '等待重试'}</li>)}</ul></details>}
    {!!data?.nextAttemptAt && !active && <p className="feishu-sync-description">下次自动重试：{time(data.nextAttemptAt)}</p>}
  </section>;
}
